import { Input, Select } from '@bifeai/ui';
import { useState } from 'react';

/** Rango de fechas (días de Argentina, AAAA-MM-DD). Vacío = sin límite. */
export interface Range {
  from: string;
  to: string;
}

export const RANGE_PRESETS = ['today', 'week', 'month', 'lastMonth', 'last30', 'last90', 'year', 'lastYear', 'all', 'custom'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];
const LABELS: Record<RangePreset, string> = {
  today: 'Hoy',
  week: 'Esta semana',
  month: 'Este mes',
  lastMonth: 'Mes pasado',
  last30: 'Últimos 30 días',
  last90: 'Últimos 90 días',
  year: 'Este año',
  lastYear: 'Año pasado',
  all: 'Todo',
  custom: 'Fechas exactas',
};

/** Hoy en Argentina, sin depender de la zona horaria de la computadora. */
function todayAr(): Date {
  const s = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
  return new Date(`${s}T12:00:00Z`);
}
const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86_400_000);

export function presetRange(p: RangePreset): Range {
  const t = todayAr();
  const y = t.getUTCFullYear();
  const m = t.getUTCMonth();
  switch (p) {
    case 'today':
      return { from: iso(t), to: iso(t) };
    case 'week':
      return { from: iso(addDays(t, -((t.getUTCDay() + 6) % 7))), to: iso(t) };
    case 'month':
      return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(t) };
    case 'lastMonth':
      return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
    case 'last30':
      return { from: iso(addDays(t, -29)), to: iso(t) };
    case 'last90':
      return { from: iso(addDays(t, -89)), to: iso(t) };
    case 'year':
      return { from: `${y}-01-01`, to: iso(t) };
    case 'lastYear':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default:
      return { from: '', to: '' };
  }
}

/** Texto corto del rango para títulos ("Este mes", "1/10 al 15/10/2026"). */
export function rangeLabel(preset: RangePreset, r: Range): string {
  if (preset !== 'custom') return LABELS[preset];
  const f = (s: string) => (s ? s.split('-').reverse().join('/') : '…');
  return `${f(r.from)} al ${f(r.to)}`;
}

export function useDateRange(initial: RangePreset = 'month') {
  const [preset, setPresetState] = useState<RangePreset>(initial);
  const [range, setRange] = useState<Range>(() => presetRange(initial));
  const setPreset = (p: RangePreset) => {
    setPresetState(p);
    if (p !== 'custom') setRange(presetRange(p));
  };
  const setCustom = (part: Partial<Range>) => {
    setPresetState('custom');
    setRange((r) => ({ ...r, ...part }));
  };
  const qs = (extra: Record<string, string | undefined> = {}) => {
    const p = new URLSearchParams();
    if (range.from) p.set('from', range.from);
    if (range.to) p.set('to', range.to);
    for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
    return p.toString();
  };
  return { preset, range, setPreset, setCustom, qs, label: rangeLabel(preset, range) };
}

/** Selector de período: atajos (hoy, este mes, este año…) o fechas exactas desde / hasta. */
export function DateRangePicker({ r }: { r: ReturnType<typeof useDateRange> }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      <Select
        aria-label="Período"
        value={r.preset}
        onChange={(e) => r.setPreset(e.target.value as RangePreset)}
        style={{ width: 'auto', minWidth: 170 }}
      >
        {RANGE_PRESETS.map((p) => (
          <option key={p} value={p}>
            {LABELS[p]}
          </option>
        ))}
      </Select>
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Desde
        <Input type="date" value={r.range.from} onChange={(e) => r.setCustom({ from: e.target.value })} style={{ width: 160 }} />
      </label>
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Hasta
        <Input type="date" value={r.range.to} onChange={(e) => r.setCustom({ to: e.target.value })} style={{ width: 160 }} />
      </label>
    </div>
  );
}
